import { Minus } from "@/components/Icons/utility/Minus.tsx";
import { Plus } from "@/components/Icons/utility/Plus.tsx";
import { ChangeEvent, useState } from "react";

interface NumInputProps {
  value?: number;
  label?: string;
  incrementDisabled?: boolean;
  decrementDisabled?: boolean;
  disabled?: boolean;
  disableFully?: boolean;
  onChange: (value: number) => void;
  className?: string;
  max?: number;
  min?: number;
}

/**
 * NumInput Component
 *
 * A reusable React component for handling positive numbers.
 * a numeric input with increment and decrement buttons.
 *
 * innerValue - State used for non-controlled input state
 *
 * @param props - The properties passed to the component.
 * @param props.initialValue - The initial numeric value of the input.
 * @param props.label - Optional label to describe the purpose of the numeric input.
 * @param props.incrementDisabled - If true, disables the increment button.
 * @param props.decrementDisabled - If true, disables the decrement button.
 * @param props.disabled - If true, disables the input.
 * @param props.disableFully - If true, disables the input and the increment/decrement buttons.
 * @param props.onChange - Callback function called when the value changes.
 *
 *  */
const NumInput = (props: NumInputProps) => {
  const { value, label, incrementDisabled, decrementDisabled, disableFully, disabled, onChange } = props;

  const [innerValue, setInnerValue] = useState(value ?? 0);

  const handleIncrement = () => {
    const currValue = value === 0 || value ? value : innerValue;
    onChange(currValue + 1);
    setInnerValue(innerValue + 1);
  };
  const handleDecrement = () => {
    const currValue = value === 0 || value ? value : innerValue;
    onChange(Math.max(currValue - 1, 0));
    setInnerValue(Math.max(innerValue - 1, 0));
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value, 10) || 0;

    setInnerValue(Math.max(newValue, 0));

    return newValue;
  };

  const handleValueChange = () => {
    setInnerValue((prev) => Math.max(Math.min(prev, props.max ?? Infinity), props.min ?? 0));

    onChange(Math.max(Math.min(innerValue, props.max ?? Infinity), props.min ?? 0));
  };

  const getDecrementDisabled = () => {
    if (decrementDisabled ?? disableFully) return true;
    if (value === 0 || value) return value === 0;
    return innerValue === 0;
  };

  return (
    <div className={`numInputWrap c-be-number-input-wrapper ${props.className}`}>
      {label && <label className="numInputLabel u-p1 c-be-number-input-label">{label}</label>}
      <div className="numInputInnerWrap c-be-number-input">
        <button
          className="numInputDecrement c-be-number-input__button c-be-number-input__button--minus"
          onClick={handleDecrement}
          disabled={getDecrementDisabled()}
        >
          <Minus />
        </button>
        <input
          type="number"
          className="numInput c-be-number-input__input"
          value={Number(innerValue).toString()}
          onChange={handleChange}
          onBlur={handleValueChange}
          onKeyDown={(e) => e.key === "Enter" && handleValueChange()}
          disabled={disabled ?? disableFully}
          max={props.max}
          min={props.min}
        />
        <button
          className="numInputIncrement c-be-number-input__button c-be-number-input__button--plus"
          onClick={handleIncrement}
          disabled={incrementDisabled ?? disableFully}
        >
          <Plus />
        </button>
      </div>
    </div>
  );
};

export default NumInput;
